import { useState } from 'react'
import { generatePath, useNavigate, useOutletContext } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { PopupDialog } from '@/components/popup-dialog'
import {
  useLosePreProgrammedDeal,
  usePostponePreProgrammedDeal,
  useStartPreProgrammedDeal,
} from '@/api/accounting-dealer'
import { ApiError } from '@/api/http-client'
import { COMMERCIAL_PHONE } from '@/constants/phones'
import { formatDate, toast } from '@/helpers'
import { routes } from '@/router'
import { PreProgrammedState, ReadDealResponse } from '@/types'

const COMMERCIAL_WHATSAPP = `https://api.whatsapp.com/send?phone=${COMMERCIAL_PHONE}`

const postponeReasonSchema = z.object({
  reason: z.string().trim().min(1, 'Conte o motivo da postergação'),
})

type PostponeReasonForm = z.infer<typeof postponeReasonSchema>

export function PreContractPage() {
  const { deal, pre_programmed: state } = useOutletContext<ReadDealResponse>()
  const [declining, setDeclining] = useState(false)
  const [postponeOpen, setPostponeOpen] = useState(false)
  const [loseOpen, setLoseOpen] = useState(false)

  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { mutate: startOpening, isPending: isStarting } =
    useStartPreProgrammedDeal({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['deal', deal.key] })
        navigate(
          generatePath(routes.accounting.hiring.mainPartner, { deal_key: deal.key }),
        )
      },
      onError: (error) => {
        toastPreProgrammedError(error, 'Não foi possível iniciar a abertura')
      },
    })

  const { mutate: postponeOpening, isPending: isPostponing } =
    usePostponePreProgrammedDeal({
      onSuccess: () => {
        toast.success('Adicionamos 3 meses ao seu prazo.')
        setPostponeOpen(false)
        setDeclining(false)
        queryClient.invalidateQueries({ queryKey: ['deal', deal.key] })
      },
      onError: (error) => {
        toastPreProgrammedError(error, 'Não foi possível adicionar 3 meses')
      },
    })

  const { mutate: loseOpening, isPending: isLosing } = useLosePreProgrammedDeal(
    {
      onSuccess: () => {
        setLoseOpen(false)
        setDeclining(false)
        queryClient.invalidateQueries({ queryKey: ['deal', deal.key] })
        queryClient.invalidateQueries({ queryKey: ['deals'] })
      },
      onError: (error) => {
        toastPreProgrammedError(error, 'Não foi possível encerrar a abertura')
      },
    },
  )

  if (!state) {
    return (
      <Box textAlign="center">
        <Typography variant="h3" mb={4} mt={4}>
          Não encontramos os dados desta abertura.
        </Typography>
      </Box>
    )
  }

  return (
    <Box textAlign="center">
      {state.lost ? (
        <LostState />
      ) : !state.can_start ? (
        <NotOpenState state={state} />
      ) : declining ? (
        <DeclineState
          canPostpone={state.can_postpone}
          onBack={() => setDeclining(false)}
          onPostpone={() => setPostponeOpen(true)}
          onLose={() => setLoseOpen(true)}
        />
      ) : (
        <FormState
          state={state}
          isStarting={isStarting}
          onStart={() => startOpening(deal.key)}
          onDecline={() => setDeclining(true)}
        />
      )}

      {postponeOpen && (
        <PostponeDialog
          loading={isPostponing}
          onClose={() => {
            if (!isPostponing) setPostponeOpen(false)
          }}
          onConfirm={(reason) => postponeOpening({ dealKey: deal.key, reason })}
        />
      )}

      <PopupDialog
        open={loseOpen}
        title="Não seguir com a abertura"
        message="A multa de R$ 1.500,00 prevista na cláusula 54.1.1 será aplicada e a abertura da PJ será encerrada."
        buttonTexts={{ cancel: 'Voltar', confirm: 'Estou ciente da multa' }}
        destructive
        confirmLoading={isLosing}
        callbacks={{
          onClose: () => {
            if (!isLosing) setLoseOpen(false)
          },
          onConfirm: () => {
            if (!isLosing) loseOpening(deal.key)
          },
        }}
      />
    </Box>
  )
}

function FormState({
  state,
  isStarting,
  onStart,
  onDecline,
}: {
  state: PreProgrammedState
  isStarting: boolean
  onStart: () => void
  onDecline: () => void
}) {
  return (
    <>
      <Typography variant="h3" mb={2} mt={4}>
        Olá!
      </Typography>
      <Card
        component={Stack}
        spacing={2}
        maxWidth={560}
        ml="auto"
        mr="auto"
        padding="24px !important"
        textAlign="left"
      >
        <Typography>
          Estamos iniciando a abertura da sua PJ, e precisamos que você confira
          algumas informações para poder seguir.
        </Typography>

        <Typography variant="h6">Prazos importantes</Typography>
        <Typography>
          É preciso iniciar este processo até, no máximo, a data da sua
          graduação sinalizada aqui. Do contrário, a multa prevista em contrato
          será aplicada.
        </Typography>
        <Typography>
          Uma vez iniciado, você tem até 30 dias para completar todas as etapas.
          Ao final desse período, será preciso iniciar novamente (desde que
          antes da data da graduação), para garantir que seus dados estejam
          atualizados.
        </Typography>
        <Typography>
          Nossa equipe está à disposição para ajudar com quaisquer pontos ou
          dúvidas que você tenha, a qualquer momento do processo. Note que isso
          não altera os prazos acima.
        </Typography>

        <ScheduleMeetingButton />

        <Typography>
          Sua data de graduação: {formatCivilDate(state.graduation_date)}
        </Typography>
        {state.postponed && (
          <Typography>
            Novo prazo para abertura:{' '}
            {formatCivilDate(state.company_opening_deadline)}
          </Typography>
        )}

        <Stack spacing={1.5}>
          <LoadingButton size="large" loading={isStarting} onClick={onStart}>
            Iniciar abertura
          </LoadingButton>
          <Button size="large" variant="outlined" onClick={onDecline}>
            Não vou seguir com a abertura
          </Button>
        </Stack>
      </Card>
    </>
  )
}

function DeclineState({
  canPostpone,
  onBack,
  onPostpone,
  onLose,
}: {
  canPostpone: boolean
  onBack: () => void
  onPostpone: () => void
  onLose: () => void
}) {
  return (
    <>
      <Typography variant="h3" mb={2} mt={4}>
        Tem certeza que não irá seguir com a abertura da PJ?
      </Typography>
      <Card
        component={Stack}
        spacing={2}
        maxWidth={560}
        ml="auto"
        mr="auto"
        padding="24px !important"
        textAlign="left"
      >
        <Typography>
          Ao contratar a Alume Mesada com a redução de juros, você optou por
          pré-contratar o serviço de contabilidade. Conforme a cláusula
          contratual 54.1.1, a multa rescisória caso decida não seguir é de R$
          1.500,00 (mil e quinhentos reais).
        </Typography>
        {canPostpone && (
          <Typography>
            Você também pode, uma única vez, adicionar 3 (três) meses ao prazo,
            ou seja, efetuar a abertura da PJ três meses após a data da sua
            graduação.
          </Typography>
        )}
        <Stack spacing={1.5}>
          <Button size="large" variant="outlined" onClick={onBack}>
            Mudei de ideia
          </Button>
          {canPostpone && (
            <Button size="large" onClick={onPostpone}>
              Adicionar 3 meses ao prazo
            </Button>
          )}
          <Button size="large" color="error" onClick={onLose}>
            Não vou seguir e estou ciente da multa
          </Button>
        </Stack>
      </Card>
    </>
  )
}

function NotOpenState({ state }: { state: PreProgrammedState }) {
  return (
    <>
      <Typography variant="h3" mb={2} mt={4}>
        O formulário ainda não está aberto
      </Typography>
      <Card
        component={Stack}
        spacing={2}
        maxWidth={560}
        ml="auto"
        mr="auto"
        padding="24px !important"
        textAlign="left"
      >
        <Typography>
          Sua data de graduação: {formatCivilDate(state.graduation_date)}
        </Typography>
        <Typography>
          Prazo para abertura: {formatCivilDate(state.company_opening_deadline)}
        </Typography>
        {state.form_opens_at && (
          <Typography>
            O formulário abre em {formatCivilDate(state.form_opens_at)}.
          </Typography>
        )}
        <ScheduleMeetingButton />
      </Card>
    </>
  )
}

function LostState() {
  return (
    <>
      <Typography variant="h3" mb={2} mt={4}>
        Abertura encerrada
      </Typography>
      <Card
        component={Stack}
        spacing={2}
        maxWidth={560}
        ml="auto"
        mr="auto"
        padding="24px !important"
        textAlign="left"
      >
        <Typography>
          Você decidiu não seguir com a abertura da PJ. A multa prevista em
          contrato será aplicada.
        </Typography>
        <Button
          size="large"
          component="a"
          href={COMMERCIAL_WHATSAPP}
          target="_blank"
          rel="noopener noreferrer"
        >
          Falar com o comercial
        </Button>
      </Card>
    </>
  )
}

function ScheduleMeetingButton() {
  return (
    <Button
      size="large"
      variant="outlined"
      component="a"
      href={COMMERCIAL_WHATSAPP}
      target="_blank"
      rel="noopener noreferrer"
    >
      Agendar reunião
    </Button>
  )
}

function PostponeDialog({
  loading,
  onClose,
  onConfirm,
}: {
  loading: boolean
  onClose: () => void
  onConfirm: (reason: string) => void
}) {
  const { control, handleSubmit } = useForm<PostponeReasonForm>({
    resolver: zodResolver(postponeReasonSchema),
    defaultValues: { reason: '' },
  })

  return (
    <Dialog
      open
      fullWidth
      maxWidth="xs"
      component="form"
      onSubmit={handleSubmit(({ reason }) => onConfirm(reason))}
      onClose={onClose}
    >
      <DialogTitle>Adicionar 3 meses ao prazo</DialogTitle>
      <DialogContent>
        <DialogContentText mb={2}>
          Você pode fazer isso uma única vez. Confirme para somar 3 meses ao
          prazo de abertura.
        </DialogContentText>
        <Controller
          name="reason"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              autoFocus
              fullWidth
              label="Motivo"
              error={Boolean(error)}
              helperText={error?.message}
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" size="small" onClick={onClose}>
          Não
        </Button>
        <LoadingButton type="submit" size="small" loading={loading}>
          Sim
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

function formatCivilDate(value: string | null) {
  if (!value) return 'não informada'
  return formatDate(value)
}

function toastPreProgrammedError(error: ApiError, fallback: string) {
  if (error.code === 'pre_programmed_start_not_allowed') {
    toast.error(
      'A abertura ainda não pode ser iniciada. O formulário abre quando faltam menos de 6 meses para o prazo.',
    )
    return
  }

  if (error.code === 'pre_programmed_postpone_window') {
    toast.error(
      'Só é possível adicionar 3 meses quando faltam 3 meses ou menos para o prazo.',
    )
    return
  }

  toast.error(error.message || fallback)
}
